<template>
  <div>
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="#00004d" size="40"></v-progress-circular>
      <p class="mt-3 grey--text">Loading Owners…</p>
    </div>
    <div v-else-if="!displayOwners.length" class="text-center py-8">
      <p class="mb-0"><i>No Ownership History Found.</i></p>
    </div>
    <div v-else class="owner-history-list">
      <v-card
        v-for="(row, idx) in displayOwners"
        :key="'oh-' + (row.id || idx) + '-' + (row.date || '') + '-' + (row.ended_at || 'current')"
        outlined
        class="mb-3 owner-history-card"
        :class="{ 'owner-history-card--current': row.is_current }"
      >
        <v-card-text class="py-3">
          <div class="d-flex align-center flex-wrap">
            <a
              class="owner-history-card__name font-weight-bold text-decoration-none"
              :href="'/contacts-management/contacts?contact=' + row.id"
              target="_blank"
            >{{ displayName(row) }}</a>
            <v-chip
              v-if="row.is_current"
              class="ml-2"
              x-small
              color="success"
              text-color="white"
              label
            >
              Current
            </v-chip>
          </div>
          <p v-if="row.uid" class="mb-0 mt-1 grey--text text--darken-1">
            ID: {{ String(row.uid).toUpperCase() }}
          </p>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ListingOwnersHistory',
  props: {
    owners: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    // Same rules as BA Property Owners: collapse twins, then Current → newest period → name.
    displayOwners() {
      const collapsed = this.collapseDuplicateOwners(
        Array.isArray(this.owners) ? this.owners : []
      );
      return this.sortOwnersLatestFirst(collapsed);
    }
  },
  methods: {
    displayName(row) {
      return this.normalizeOwnerName((row && row.full_name) || '') || '—';
    },
    normalizeOwnerName(name) {
      return String(name || '')
        .replace(/\s+/g, ' ')
        .trim();
    },
    ownershipYear(row) {
      const value = row && row.date;
      if (!value) {
        return 0;
      }
      const raw = String(value).trim();
      const ts = Date.parse(raw);
      if (!Number.isFinite(ts)) {
        return 0;
      }
      const year = new Date(ts).getUTCFullYear();
      return year >= 1990 && year <= 2100 ? year : 0;
    },
    dateSortValue(value) {
      if (!value) {
        return 0;
      }
      const raw = String(value).trim();
      const ts = Date.parse(raw);
      if (Number.isFinite(ts)) {
        return ts;
      }
      const dayTs = Date.parse(raw.slice(0, 10));
      return Number.isFinite(dayTs) ? dayTs : 0;
    },
    // Match BA collapseDuplicateOwnerCards: same contact_id or exact same name → one card.
    collapseDuplicateOwners(owners) {
      if (!owners.length) {
        return [];
      }

      const rankedIndexes = owners.map((_, index) => index);
      rankedIndexes.sort((ia, ib) => {
        const a = owners[ia];
        const b = owners[ib];
        const ac = a && a.is_current ? 1 : 0;
        const bc = b && b.is_current ? 1 : 0;
        if (ac !== bc) {
          return bc - ac;
        }
        const ay = this.ownershipYear(a);
        const by = this.ownershipYear(b);
        if (ay !== by) {
          return by - ay;
        }
        const idCmp = (Number(a && a.id) || 0) - (Number(b && b.id) || 0);
        if (idCmp !== 0) {
          return idCmp;
        }
        return ia - ib;
      });

      const winnerIndexByContact = {};
      const winnerIndexByName = {};
      rankedIndexes.forEach((index) => {
        const owner = owners[index];
        const contactId = Number(owner && owner.id) || 0;
        const nameKey = this.normalizeOwnerName((owner && owner.full_name) || '').toLowerCase();
        if (contactId > 0 && winnerIndexByContact[contactId] === undefined) {
          winnerIndexByContact[contactId] = index;
        }
        if (nameKey && winnerIndexByName[nameKey] === undefined) {
          winnerIndexByName[nameKey] = index;
        }
      });

      const out = [];
      const emittedContact = {};
      const emittedName = {};
      owners.forEach((owner, index) => {
        const contactId = Number(owner && owner.id) || 0;
        const nameKey = this.normalizeOwnerName((owner && owner.full_name) || '').toLowerCase();

        if (contactId > 0 && (winnerIndexByContact[contactId] ?? index) !== index) {
          return;
        }
        if (nameKey && (winnerIndexByName[nameKey] ?? index) !== index) {
          return;
        }
        if (contactId > 0 && emittedContact[contactId]) {
          return;
        }
        if (nameKey && emittedName[nameKey]) {
          return;
        }

        if (contactId > 0) {
          emittedContact[contactId] = true;
        }
        if (nameKey) {
          emittedName[nameKey] = true;
        }
        out.push(owner);
      });

      return out;
    },
    // Match BA sortOwnersLatestFirst (history path).
    sortOwnersLatestFirst(owners) {
      const list = owners.slice();
      list.sort((a, b) => {
        const aCurrent = !!(a && a.is_current);
        const bCurrent = !!(b && b.is_current);
        if (aCurrent !== bCurrent) {
          return aCurrent ? -1 : 1;
        }

        const aSort = aCurrent
          ? this.dateSortValue(a && a.date)
          : (this.dateSortValue(a && a.ended_at) || this.dateSortValue(a && a.date));
        const bSort = bCurrent
          ? this.dateSortValue(b && b.date)
          : (this.dateSortValue(b && b.ended_at) || this.dateSortValue(b && b.date));

        if (aSort !== bSort) {
          if (!aSort) {
            return 1;
          }
          if (!bSort) {
            return -1;
          }
          return bSort - aSort;
        }

        return this.normalizeOwnerName((a && a.full_name) || '').localeCompare(
          this.normalizeOwnerName((b && b.full_name) || ''),
          undefined,
          {sensitivity: 'base'}
        );
      });
      return list;
    }
  }
};
</script>

<style scoped>
.owner-history-card--current {
  border-color: #4caf50 !important;
  background: rgba(76, 175, 80, 0.04);
}
.owner-history-card__name {
  font-size: 1.05rem;
  line-height: 1.35;
  color: #00004d;
}
</style>
